import { useTranslation } from "react-i18next";
import casesService from "api/casesService";
import fileService from "api/fileService";
import listService from "api/listService";
import ActionsCalendar from "components/ActionsCalendar";
import ActionsList from "components/ActionsList";
import CaseChat from "components/CaseChat";
import CaseDossier from "components/CaseDossier";
import CaseHeader from "components/CaseHeader";
import CaseMenu from "components/CaseMenu";
import EmptyPanel from "components/EmptyPanel";
import { RedirectModal } from "components/RedirectModal";
import Tasks from "components/Tasks";
import { config } from "config";
import CasesContext from "contexts/CasesContext";
import KeysContext from "contexts/KeysContext";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import { IKey } from "dto/IKey";
import { ItemKind } from "dto/IKindItems";
import { IPackage } from "dto/IPackage";
import { IStatus } from "dto/IStatus";
import { ITag } from "dto/ITag";
import usePDF from "hooks/usePDF";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ECaseViews } from "./dto/ECaseViews";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import { AbilityContext } from "contexts/AbilityContext";
import PackagesContext from "contexts/PackagesContext";
import useGetPDFViewer from "hooks/useGetPDFViewer";
import DossierFilesContext from "contexts/DossierFilesContext";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import PluginNotExist from "components/PluginNotExist";
import { replaceFileExtension } from "utils/replaceFileExtension";
import PDFViewer from "components/PDFViewer";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import AttachToCaseModalAttachments from "components/AttachCaseModalNew/AttachToCaseModalAttachments";
import ModalsManagerContext from "contexts/ModalsManager";
import CaseNotes from "components/CaseNotes";
import CaseCourtPortal from "components/CaseCourtPortal";

const CaseView: FC<Props> = ({
  caseIdFromProps,
  tags,
  statuses,
  hideMenu = false,
  showCase = false,
  forceMenu = false,
  hideOptions = false,
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  const {
    caseId: caseIdFromParams,
    view: viewFromParams,
    kind,
    messageId,
    attachmentId,
  } = useParams();
  const caseId = caseIdFromProps ?? caseIdFromParams;
  const [view, setView] = useState<ECaseViews>(
    !caseIdFromProps
      ? (viewFromParams as ECaseViews) ?? ECaseViews.CHAT
      : ECaseViews.CHAT,
  );
  const [showRedirect, setShowRedirect] = useState(false);
  const [, setCurrentDossierFolder] =
    useState<IDossierItem | null>(null);
  const option: any = localStorage.getItem(config.cases_actions);
  const {
    blob,
    create,
    removeBlob,
    showInstallPlugin,
    setShowInstallPlugin,
    attachment,
  } = usePDF();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const PDFId = queryParams.get("pdf");
  const fullscreen = queryParams.get("fullscreen");
  const ability = useContext(AbilityContext);
  const { packages, setPackages } = useContext(PackagesContext);
  const { dossierFiles } = useContext(DossierFilesContext);

  const pdfRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (pdfRef.current) {
      console.log("adding show class");
      pdfRef.current.classList.add(styles.show);
    }
  }, [pdfRef.current]);

  useEffect(() => {
    if (!PDFId) {
      if (blob) {
        removeBlob();
      }
      return;
    }

    if (fullscreen) return;

    const splittedPDFId = PDFId.split(":");

    const packId = splittedPDFId[0];
    const fileName = splittedPDFId[1];
    const fileUri = splittedPDFId[2];
    const fileKind = splittedPDFId[3];
    const fileEncryptionKey = splittedPDFId[4];

    console.log(
      "Preview in case",
      fileName,
      fileUri,
      fileKind,
      fileEncryptionKey,
    );

    const attach = packages
      .reduce((a, v) => a.concat(v.Attachments ?? []), [])
      .find(
        (attachment) =>
          attachment.Uri === fileUri ||
          attachment.Id === fileUri ||
          attachment.Id === packId,
      );

    if (attach) {
      create(fileUri, attach, Number(fileKind) as ItemKind);
      setUnusedAttachments([attach]);
    } else {
      const di = dossierFiles.find(
        (d) => d.FileUri == fileUri || d.SignedPdfUri == fileUri,
      );

      if (di) {
        create(
          di.AttachmentParentId ?? fileUri,
          {
            Id: di.DossierId,
            Uri: di.SignedPdfUri ? di.SignedPdfUri : di.FileUri,
            FileName: di.SignedPdfUri
              ? replaceFileExtension(di.FileName, ".pdf")
              : di.FileName,
            ContentType: di.FileContentType,
            Format: di.FileContentType,
            Kind: Number(fileKind),
            Label: di.Label,
            Size: di.FileSize,
            CommentsCount: di.CommentsCount,
            CreationDate: di.CreationDate,
            DeskInfo_IsOn: false,
            DossierId: di.DossierId,
            ShowInDossier: 0,
            Type: DossierItemType.Attachment,
            LastModified: di.LastModified,
            IsShared: di.IsShared,
            TagCount: di.TagCount,
            EncryptionKeyId: fileEncryptionKey,
            Keys: [],
            ObjectKeys: [],
          },
          Number(fileKind) as ItemKind,
        );
      } else if (Number(fileKind) === ItemKind.CourtPortalDeliverance) {
        create(
          packId,
          {
            Id: packId,
            Uri: null,
            FileName: fileName,
            ContentType: "application/pdf",
            Format: ".pdf",
            Kind: Number(fileKind),
            Label: "",
            Size: 0,
            CommentsCount: 0,
            CreationDate: null,
            DeskInfo_IsOn: false,
            DossierId: packId,
            ShowInDossier: 0,
            Type: DossierItemType.Attachment,
            LastModified: null,
            IsShared: false,
            TagCount: 0,
            EncryptionKeyId: fileEncryptionKey,
            Keys: [],
            ObjectKeys: [],
          },
          ItemKind.CourtPortalDeliveranceDocument as ItemKind,
        );
      }
    }
  }, [PDFId, location, dossierFiles]);

  const [actionsView] = useState<
    | "dayGridWeek"
    | "dayGridMonth"
    | "dayGridDay"
    | "listWeek"
    | "list"
    | "tasks"
  >(option ?? "dayGridMonth");

  const { cases: casesContext, setCases: setCasesContext } =
    useContext(CasesContext);
  const { data: caseData, isSuccess: caseDataSuccess } =
    casesService.useGetCase(caseId ?? "");

  const optionsRef = useRef(null);
  const Case = useMemo(() => (caseDataSuccess ? caseData : null), [caseData]);

  const [showPDF, setShowPDF] = useState<Blob | string | null>(null);
  const [originalFile, setOriginalFile] = useState<Blob | string | null>(null);

  const { data: previewPackageData } = listService.useGetPackage({
    FilterId: messageId ?? "",
    Kind: kind ? Number(kind) : ItemKind.None,
    enabled: !hideMenu && !!messageId,
  });

  const [attachmentsLocation, setAttachmentsLocation] =
    useState<string>("center");

  const { options } = useContext(settingsContext);

  useEffect(() => {
    if (options) {
      const attL = options.find(
        (x) => x.Type === IOptionType.caseAttachAttachmentsLocation,
      );

      if (attL) {
        setAttachmentsLocation(attL.Value);
      }
    }
  }, [options]);

  useEffect(() => {
    setShowPDF(null);
    setOriginalFile(null);
  }, [messageId, attachmentId]);

  const [previewPackageInt, setPreviewPackageInt] = useState<IPackage>(null);

  const previewPackage: IPackage = useMemo(
    () =>
      (previewPackageData?.data?.Items?.[0] as IPackage) ?? previewPackageInt,
    [previewPackageData, previewPackageInt],
  );
  const {
    modals,
    setModals,
    setOptions: setModalOptions,
  } = useContext(ModalsManagerContext);
  const [showComments, setShowComments] = useState(false);
  const { getAnnotationToAdd } = useGetPDFViewer();

  const [usedAttachments, setUsedAttachments] = useState<
    IAttachToCaseFolderLink[]
  >([]);
  const [unusedAttachments, setUnusedAttachments] = useState<IAttachment[]>([]);

  useEffect(() => {
    if (showComments || !getAnnotationToAdd()) return;
    setShowComments(true);
  }, [getAnnotationToAdd()]);

  fileService.useGetFile();
  const parentKeys: null | IKey[] = useMemo(
    () => previewPackageData?.data.Keys ?? null,
    [previewPackageData],
  );

  const { keys, setKeys } = useContext(KeysContext);

  const [createFolder, setCreateFolder] = useState(false);
  const handleCreateFolder = () => {
    if (view === ECaseViews.DOSSIER) {
      setCreateFolder(true);
    } else {
      setCreateFolder(true);

      const link = `/cases/${caseId}/${ECaseViews.DOSSIER}`;
      navigate(`${link}`);
    }
  };

  const handleCreateEditorDocument = (format: string) => {
    // if (isPluginError) {
    //   setShowPluginError(true);
    // } else {
    setModalOptions({ format: format, callback: () => {} });
    setModals({ ...modals, createLetter: true, hide: false });
    //}
  };

  useEffect(() => {
    if (Object.values(ECaseViews).includes(viewFromParams)) {
      console.log("View update", viewFromParams);
      setView(viewFromParams as ECaseViews);
    }
  }, [viewFromParams]);

  const lastViewSettingsView = () => `caseLastTab_${Case?.Id}`;

  useEffect(() => {
    if (Case?.Id) {
      const idx = window.localStorage.getItem(lastViewSettingsView());

      if (idx) {
        setView(idx as ECaseViews);
        const link = `/cases/${caseId}/${idx}`;

        const query = new URLSearchParams(window.location.search);

        if (PDFId && !query.get("pdf")) {
          query.append("pdf", PDFId);
        }

        if (isFullScreen && !query.get("fullscreen")) {
          query.append("fullscreen", "1");
        }

        navigate(`${link}?${query}`);
      }
    }
  }, [Case?.Id]);

  useEffect(() => {
    window.localStorage.setItem(lastViewSettingsView(), view);
  }, [view]);

  useEffect(() => {
    if (!caseData) return;
    //@ts-ignore
    const k = caseData as any;

    let uploadKeys: any = [];

    if (k) {
      uploadKeys = [...uploadKeys, keys].flat();
    }

    if (parentKeys) {
      uploadKeys = [...uploadKeys, parentKeys].flat();
    }

    if (isFullScreen) {
      setCasesContext([Case!, ...casesContext]);
    }

    //@ts-ignore
    setKeys((prev) => [...prev, uploadKeys].flat());
  }, [caseData, parentKeys]);

  const [showVersion] = useState(false);

  casesService.useDeleteCase();

  const navigate = useNavigate();

  const handleMenuClick = (view: ECaseViews) => {
    console.log("Menu click", view);
    if (caseIdFromProps) {
      setView(view);
    }
  };

  const { mutate: manageDossier } = listService.useManageDossier();

  const handleDossierSave = () => {
    manageDossier({
      AttachmentIds: usedAttachments.map((x) => x.AttachmentId),
      OwnerId: previewPackage.Id,
      OwnerKind: previewPackage.Kind,
      Show: true,
      Links: usedAttachments,
    });

    const newPacks = [...packages];

    usedAttachments.forEach((a) => {
      const el = newPacks.find((p) => p.Id === previewPackage.Id);
      if (el) {
        const attach = el.Attachments.find((e) => e.Id === a.AttachmentId);

        if (attach) {
          attach.ShowInDossier = 1;
        }
      }
    });

    setPackages(newPacks);
  };

  const [selectedDate] = useState(new Date());

  const [sort] = useState(
    localStorage.getItem("chatSort") ?? "Ascending",
  );

  const contentPanel = () => {
    console.log(view);

    if (!caseId?.length) {
      return <EmptyPanel />;
    }

    if (view === ECaseViews.PARTIAL) {
      return (
        <div className={styles.access}>
          <div className={styles.titlee}>{t("cases.sharedCase")}</div>
          <div className={styles.body}>
            {t("cases.waitingForShare")}
          </div>
        </div>
      );
    }

    if (view === ECaseViews.TASKS && ability.can("accessModule", "tasks")) {
      return <Tasks />;
    }

    if (view === ECaseViews.LIST) {
      return (
        <ActionsList date={new Date()} caseId={caseId} listType="events" />
      );
    }

    if (view === ECaseViews.NOTES) {
      return <CaseNotes caseId={caseId} />;
    }

    if (
      ability.can("accessModule", "tasks") &&
      (view === ECaseViews.ACTION_DAY ||
        view === ECaseViews.ACTION_MONTH ||
        view === ECaseViews.ACTION_WEEK)
    ) {
      return (
        <ActionsCalendar
          initialValues={{
            case: caseData,
          }}
          showCase={false}
          type={(() => {
            switch (view) {
              case ECaseViews.ACTION_DAY:
                return "dayGridDay";
              case ECaseViews.ACTION_WEEK:
                return "dayGridWeek";
              case ECaseViews.ACTION_MONTH:
                return "dayGridMonth";
              default:
                return "dayGridDay";
            }
          })()}
          date={selectedDate}
          caseId={caseId}
        />
      );
    }

    if (view === ECaseViews.CHAT || view === ECaseViews.PACKAGES) {
      return (
        <CaseChat
          caseId={caseId}
          tags={tags}
          statuses={statuses}
          createNewThread={false}
          setCreateNewThread={() => {}}
          setSelectedThread={() => {}}
          thread={null}
          hideMenu={hideMenu}
          setPack={() => {}}
          sort={sort}
        />
      );
    }

    if (view === ECaseViews.COURTPORTAL && Case?.Id) {
      return (
        <CaseCourtPortal
          caseId={Case?.Id}
          handleAddToDossier={(pack) => {
            setPreviewPackageInt(pack);
            setUnusedAttachments(pack.Attachments);
            setUsedAttachments([]);
            setShowAddToDossier(true);
          }}
        ></CaseCourtPortal>
      );
    }

    if (view === ECaseViews.DOSSIER && ability.can("accessModule", "drive")) {
      return (
        <CaseDossier
          setCurrentFolderToCaseView={setCurrentDossierFolder}
          Case={Case}
          hideCreate={true}
          createFolder={createFolder}
          setCreateFolder={setCreateFolder}
        />
      );
    }

    // if (view === ECaseViews.ACTIONS) {
    //   if (actionsView === "tasks") {
    //     return <Tasks />;
    //   }
    //   if (actionsView === "list") {
    //     return <ActionsList date={new Date()} caseId={caseId} />;
    //   }
    //   return (
    //     <ActionsCalendar
    //       initialValues={{
    //         case: caseData,
    //       }}
    //       showCase={false}
    //       type={actionsView}
    //       date={selectedDate}
    //       caseId={caseId}
    //     />
    //   );
    // }
  };

  const [, setStatus] = useState<IStatus | null>(null);
  const [, setFindedTags] = useState<ITag[]>([]);
  const [showAddToDossier, setShowAddToDossier] = useState<boolean>(false);

  const handleAddToDossier = () => {
    setShowAddToDossier(true);
  };

  useEffect(() => {
    if (Case) {
      const newStatus = statuses?.find((s: IStatus) => s.Id === Case.StatusId);
      setStatus(newStatus ?? null);

      const t = tags?.filter((tag: ITag) =>
        Case?.TagIds?.find((iTag) => tag.Id === iTag),
      );
      setFindedTags(t);
    }
  }, [casesContext, Case, Case?.TagIds]);

  let caseContext = casesContext.find((c) => c.Id === Case?.Id);

  if (!caseContext && Case) {
    caseContext = Case;
  }

  const isFullScreen = window.location.href.search("fullscreen") !== -1;

  return (
    <>
      <div className={styles.layout}>
        {!caseId?.length && <EmptyPanel></EmptyPanel>}

        {!!caseId?.length && showCase && !hideHeader && (
          <CaseHeader
            hideOptions={hideOptions}
            optionsRef={optionsRef}
            Case={Case}
            statuses={statuses}
          />
        )}

        {!!caseId?.length && (
          <>
            {((!showPDF &&
              !blob &&
              view !== ECaseViews.LETTER &&
              showCase &&
              view !== ECaseViews.PARTIAL) ||
              forceMenu) &&
              Case?.Id && (
                <div className={styles.menuWrapper}>
                  <CaseMenu
                    activeView={view}
                    onClick={caseIdFromProps ? handleMenuClick : undefined}
                    handleCreateFolder={handleCreateFolder}
                    handleCreateEditorDocument={handleCreateEditorDocument}
                    Case={Case}
                  />
                </div>
              )}
            {showCase && view === ECaseViews.LETTER && (
              <div className={styles.menuWrapper}>
                <CaseMenu
                  activeView={view}
                  onClick={caseIdFromProps ? handleMenuClick : undefined}
                  handleCreateFolder={handleCreateFolder}
                  handleCreateEditorDocument={handleCreateEditorDocument}
                  Case={Case}
                />
              </div>
            )}
            <div
              className={`${styles.rightY} ${
                (showPDF || view === ECaseViews.LETTER) && styles.showPDF
              } ${
                view === ECaseViews.ACTIONS &&
                actionsView === "tasks" &&
                styles.remove
              }`}
            >
              <div className={styles.content}>{contentPanel()}</div>
            </div>
          </>
        )}

        {showVersion && (
          <div className={styles.PDFWrapper} ref={pdfRef}>
            <PDFViewer
              sourceFile={originalFile}
              file={new Blob([])}
              showHeader={true}
              showVersion={true}
            />
          </div>
        )}

        {blob && !isFullScreen && (
          <div className={styles.PDFWrapper} ref={pdfRef}>
            <PDFViewer
              showTitle
              file={blob}
              title={
                attachment?.Label
                  ? attachment.Label
                  : attachment?.FileName ?? ""
              }
              sourceFile={originalFile}
              showHeader={true}
              setShowComments={() => setShowComments(!showComments)}
              handleAddToDossier={handleAddToDossier}
            />
          </div>
        )}
      </div>
      {showRedirect && (
        <RedirectModal
          onCancel={() => setShowRedirect(false)}
          // onSelect={handleRedirect}
          Case={Case!}
          toggle={() => {
            setShowRedirect(false);
          }}
        />
      )}

      {showInstallPlugin && (
        <PluginNotExist
          message={t("errors.pluginRequired")}
          handleClose={() => setShowInstallPlugin(false)}
        ></PluginNotExist>
      )}

      {showAddToDossier && (
        <>
          {attachmentsLocation === "center" && (
            <ModalLayout dock={DockPosition.CENTER} showBg={true}>
              <AttachToCaseModalAttachments
                toggle={(c) => setShowAddToDossier(c)}
                c={caseData}
                pack={previewPackage}
                unusedAttachments={[attachment]}
                setUnusedAttachments={setUnusedAttachments}
                setUsedAttachments={setUsedAttachments}
                usedAttachments={[]}
                onSave={handleDossierSave}
                position="center"
              ></AttachToCaseModalAttachments>
            </ModalLayout>
          )}
          {attachmentsLocation !== "center" && (
            <ModalLayout
              dock={DockPosition.DISABLED}
              off={true}
              customX={8}
              customY={0}
              ignore
              isAnimation
              fullHeight={true}
              name="attachCase"
            >
              <div className={styles.leftModal}>
                <AttachToCaseModalAttachments
                  toggle={(c) => setShowAddToDossier(c)}
                  c={caseData}
                  pack={previewPackage}
                  unusedAttachments={unusedAttachments}
                  setUnusedAttachments={setUnusedAttachments}
                  setUsedAttachments={setUsedAttachments}
                  usedAttachments={usedAttachments}
                  onSave={handleDossierSave}
                  className={styles.nomodal}
                  position="left"
                ></AttachToCaseModalAttachments>
              </div>
            </ModalLayout>
          )}
        </>
      )}
    </>
  );
};

export default CaseView;
